import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography } from '@mui/material';

const renderManufacturerValue = (value) => {
  return value === '' ? 'All Manufacturers' : value;
};

const DateRangeField = ({ dateFilter, setDateFilter }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tempDateFilter, setTempDateFilter] = useState(dateFilter);

  const getDatePresets = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setDate(today.getDate() - 30);
    
    const formatDate = (date) => date.toLocaleDateString('en-CA');
    
    return [
      { label: 'Today', fromDate: formatDate(today), toDate: formatDate(today) },
      { label: 'Yesterday', fromDate: formatDate(yesterday), toDate: formatDate(yesterday) },
      { label: 'Last 7 Days', fromDate: formatDate(lastWeek), toDate: formatDate(today) },
      { label: 'Last 30 Days', fromDate: formatDate(lastMonth), toDate: formatDate(today) }
    ];
  };

  const getDisplayValue = () => {
    const { fromDate, toDate } = dateFilter;
    if (!fromDate && !toDate) return '';
    
    // Check if current filter matches a preset
    const presets = getDatePresets();
    const matchingPreset = presets.find(preset => 
      preset.fromDate === fromDate && preset.toDate === toDate
    );
    if (matchingPreset) return matchingPreset.label;
    
    if (fromDate && toDate) return `${fromDate} to ${toDate}`;
    if (fromDate) return `From ${fromDate}`;
    if (toDate) return `Until ${toDate}`;
    return '';
  };

  const handleApply = () => {
    setDateFilter(tempDateFilter);
    setDialogOpen(false);
  };

  const handleCancel = () => {
    setTempDateFilter(dateFilter);
    setDialogOpen(false);
  };

  const handleClear = () => {
    const clearedFilter = { fromDate: '', toDate: '' };
    setTempDateFilter(clearedFilter);
    setDateFilter(clearedFilter);
    setDialogOpen(false);
  };

  return (
    <>
      <TextField
        label="Filter by Date Range"
        value={getDisplayValue()}
        onClick={() => setDialogOpen(true)}
        sx={{ minWidth: 140, cursor: 'pointer' }}
        size="small"
        placeholder="Select date range"
        slotProps={{
          input: { readOnly: true },
          inputLabel: { shrink: true }
        }} />
      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Select Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="subtitle2" sx={{
              color: "text.secondary"
            }}>Quick Select</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {getDatePresets().map((preset) => (
                <Button
                  key={preset.label}
                  size="small"
                  variant="outlined"
                  onClick={() => setTempDateFilter({ fromDate: preset.fromDate, toDate: preset.toDate })}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  {preset.label}
                </Button>
              ))}
            </Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
                mt: 1
              }}>Custom Range</Typography>
            <TextField
              label="From Date"
              type="date"
              value={tempDateFilter.fromDate || ''}
              onChange={(e) => setTempDateFilter({ ...tempDateFilter, fromDate: e.target.value })}
              size="small"
              fullWidth
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />
            <TextField
              label="To Date"
              type="date"
              value={tempDateFilter.toDate || ''}
              onChange={(e) => setTempDateFilter({ ...tempDateFilter, toDate: e.target.value })}
              size="small"
              fullWidth
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />
            {(tempDateFilter.fromDate || tempDateFilter.toDate) && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 1
                }}>
                {tempDateFilter.fromDate && tempDateFilter.toDate 
                  ? `Showing accounts created between ${tempDateFilter.fromDate} and ${tempDateFilter.toDate}`
                  : tempDateFilter.fromDate 
                    ? `Showing accounts created from ${tempDateFilter.fromDate} onwards`
                    : `Showing accounts created until ${tempDateFilter.toDate}`
                }
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClear} color="secondary">Clear</Button>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleApply} variant="outlined">Apply</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const FilterInputs = ({ selectedManufacturer, setSelectedManufacturer, availableManufacturers, dateFilter, setDateFilter, accountNameFilter, setAccountNameFilter, tabValue, fetchPaginatedData, statusFilter, setStatusFilter, pageSize }) => {
  return (
    <>
      <Grid sx={{ minWidth: 100, flexShrink: 0 }}>
      <TextField
        select
        label="Filter by Manufacturer"
        value={selectedManufacturer}
        onChange={(e) => setSelectedManufacturer(e.target.value)}
        sx={{ minWidth: 100 }}
        size="small"
        slotProps={{
          select: {
            displayEmpty: true,
            renderValue: renderManufacturerValue
          },

          inputLabel: { shrink: true }
        }}>
        <MenuItem value="">All Manufacturers</MenuItem>
        {availableManufacturers.map(mfg => (
          <MenuItem key={mfg} value={mfg}>{mfg}</MenuItem>
        ))}
      </TextField>
    </Grid>
      <Grid sx={{ minWidth: 140, flexShrink: 0 }}>
        <DateRangeField 
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
        />
      </Grid>
      <Grid sx={{ minWidth: 100, flexShrink: 0 }}>
        <TextField
          label="Filter by Account Name"
          value={accountNameFilter}
          onChange={(e) => {
            setAccountNameFilter(e.target.value);
            if (tabValue === 1) {
              clearTimeout(window.searchTimeout);
              window.searchTimeout = setTimeout(() => {
                fetchPaginatedData(1, e.target.value, pageSize);
              }, 500);
            }
          }}
          placeholder="Account name"
          sx={{ minWidth: 100 }}
          size="small"
          slotProps={{
            inputLabel: { shrink: true }
          }}
        />
      </Grid>
      <Grid sx={{ minWidth: 100, flexShrink: 0 }}>
        <TextField
          select
          label="Filter by Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 100 }}
          size="small"
          slotProps={{
            select: {
              displayEmpty: true,
              renderValue: (value) => {
                const statusLabels = {
                  '': 'All Status',
                  'completed': 'Completed',
                  'inProgress': 'In Progress',
                  'goLive': 'Go-Live'
                };
                return statusLabels[value] || 'All Status';
              }
            },

            inputLabel: { shrink: true }
          }}>
          <MenuItem value="">All Status</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="inProgress">In Progress</MenuItem>
          <MenuItem value="goLive">Go-Live</MenuItem>
        </TextField>
        </Grid>
    </>
  );
};

FilterInputs.propTypes = {
  selectedManufacturer: PropTypes.string.isRequired,
  setSelectedManufacturer: PropTypes.func.isRequired,
  availableManufacturers: PropTypes.array.isRequired,
  dateFilter: PropTypes.object.isRequired,
  setDateFilter: PropTypes.func.isRequired,
  accountNameFilter: PropTypes.string.isRequired,
  setAccountNameFilter: PropTypes.func.isRequired,
  tabValue: PropTypes.number.isRequired,
  fetchPaginatedData: PropTypes.func.isRequired,
  statusFilter: PropTypes.string.isRequired,
  setStatusFilter: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired
};

export default FilterInputs;